import React, { useEffect, useMemo, useState } from 'react';
import Card from '@/components/ui/Card';
import { auditAPI, AuditLogItem, AuditFilters } from '@/services/api';
import DelayedLoading from '@/components/ui/DelayedLoading';
import { SkeletonTable } from '@/components/ui/LoadingSkeleton';

const PAGE_SIZE = 50;

const ACTION_COLORS: Record<string, string> = {
  login: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  login_failed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  logout: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  ai_chat: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  ai_analysis: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  report_create: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  report_update: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  report_delete: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  document_upload: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300',
  document_delete: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  calendar_create: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300',
  calendar_update: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  calendar_delete: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  player_update: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  user_create: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
  user_role_change: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  user_deactivate: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  password_change: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  impersonate_start: 'bg-pink-100 text-pink-800 dark:bg-pink-900/40 dark:text-pink-300',
  impersonate_stop: 'bg-pink-100 text-pink-800 dark:bg-pink-900/40 dark:text-pink-300',
  match_view: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
};

function actionBadge(action: string): string {
  return ACTION_COLORS[action] ?? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300';
}

function formatDateTime(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

const AuditLogPage: React.FC = () => {
  const [items, setItems] = useState<AuditLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [actions, setActions] = useState<string[]>([]);
  const [users, setUsers] = useState<Array<{ id: string; name: string; email: string }>>([]);

  const [userId, setUserId] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const filters: AuditFilters = useMemo(() => ({
    userId: userId || undefined,
    action: action || undefined,
    from: from ? new Date(from).toISOString() : undefined,
    to: to ? new Date(to).toISOString() : undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  }), [userId, action, from, to, page]);

  useEffect(() => {
    auditAPI.filters().then(res => {
      setActions(res.actions);
      setUsers(res.users);
    }).catch(() => { /* non-fatal */ });
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    auditAPI.list(filters).then(res => {
      if (!active) return;
      setItems(res.items);
      setTotal(res.total);
      setError('');
    }).catch(() => {
      if (active) setError('Failed to load audit log');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [filters]);

  const handleExport = async () => {
    try {
      const blob = await auditAPI.exportCsv({ userId: filters.userId, action: filters.action, from: filters.from, to: filters.to });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'audit-log.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to export audit log');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Audit Log</h1>
        <button
          onClick={handleExport}
          className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 text-sm"
        >
          Export CSV
        </button>
      </div>

      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 p-4">
          <select
            value={userId}
            onChange={e => { setPage(0); setUserId(e.target.value); }}
            className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 text-sm"
          >
            <option value="">All users</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select
            value={action}
            onChange={e => { setPage(0); setAction(e.target.value); }}
            className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 text-sm"
          >
            <option value="">All actions</option>
            {actions.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <input type="date" value={from} onChange={e => { setPage(0); setFrom(e.target.value); }}
            className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 text-sm" />
          <input type="date" value={to} onChange={e => { setPage(0); setTo(e.target.value); }}
            className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 text-sm" />
        </div>
      </Card>

      <Card>
        {loading ? (
          <DelayedLoading loading={loading}>
            <SkeletonTable label="Loading audit log" rows={5} columns={4} className="p-8" />
          </DelayedLoading>
        ) : error ? (
          <div className="p-8 text-center text-red-600 dark:text-red-400">{error}</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">No activity found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Time</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">User</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Action</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {items.map(it => (
                  <tr key={it.id}>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatDateTime(it.createdAt)}</td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                      {it.userName || 'System'}
                      {it.userEmail && <span className="block text-xs text-gray-500 dark:text-gray-400">{it.userEmail}</span>}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${actionBadge(it.action)}`}>{it.action}</span>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">{it.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
        <span>{total} total</span>
        <div className="flex gap-2">
          <button disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}
            className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40">Prev</button>
          <span>Page {page + 1} / {totalPages}</span>
          <button disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)}
            className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40">Next</button>
        </div>
      </div>
    </div>
  );
};

export default AuditLogPage;
